import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import * as pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.mjs";

// pdf.js corre en el hilo principal (fake worker): el worker viaja
// dentro del bundle y no hay archivo estático extra que desplegar.
(globalThis as any).pdfjsWorker = pdfjsWorker;

export interface AttachedFile {
  name: string;
  text: string;
  // "file": archivo adjuntado (picker / drag & drop / paste de archivo).
  // "paste": texto largo pegado o arrastrado al textarea.
  kind: "file" | "paste";
}

export const ACCEPTED_FILE_EXTENSIONS = [".pdf", ".html", ".htm", ".md", ".txt"];
export const ACCEPTED_FILE_ACCEPT = ACCEPTED_FILE_EXTENSIONS.join(",");

// Umbral en PALABRAS a partir del cual un texto pegado/arrastrado se
// convierte en adjunto (chip "Pasted text N") en lugar de insertarse
// en el textarea. Un prompt normal copiado (<= 120 palabras) se pega
// como input de siempre; los adjuntos no pasan por la normalización
// de sinónimos del backend, el input del usuario sí.
export const PASTE_ATTACHMENT_WORD_THRESHOLD = 120;

export function shouldAttachPastedText(text: string): boolean {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return words.length > PASTE_ATTACHMENT_WORD_THRESHOLD;
}

// Direct Line limita la actividad completa a 256 KB; se recorta el
// texto extraído para dejar margen al resto del mensaje.
const MAX_TEXT_LENGTH = 150000;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_FILE_EXTENSIONS.some((extension) => name.endsWith(extension));
}

async function extractPdfText(file: File): Promise<string> {
  const data = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  try {
    const pages: string[] = [];
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const pageText = content.items
        .map((item: any) => (typeof item.str === "string" ? item.str : ""))
        .join(" ");
      pages.push(pageText.trim());
    }
    return pages.join("\n\n").trim();
  } finally {
    await pdf.destroy();
  }
}

// Convierte el archivo a texto plano: HTML y Markdown se mandan tal
// cual (el HTML sirve como plantilla de artifact), los PDF vía pdf.js.
export async function extractTextFromFile(file: File): Promise<string> {
  if (!isAcceptedFile(file)) {
    throw new Error(
      `${file.name}: only PDF, HTML, Markdown and TXT files are supported`
    );
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(`${file.name}: file exceeds the 10 MB limit`);
  }
  const text = file.name.toLowerCase().endsWith(".pdf")
    ? await extractPdfText(file)
    : await file.text();
  if (text.length > MAX_TEXT_LENGTH) {
    return `${text.slice(0, MAX_TEXT_LENGTH)}\n[...content truncated...]`;
  }
  return text;
}

// Anexa el contenido de los adjuntos al final del mensaje del usuario.
// El backend separa estos bloques con agentic/attachments.py (no les
// aplica sinónimos ni lowercase); mantener ambos formatos en sync.
export function buildMessageWithAttachments(
  message: string,
  files: AttachedFile[]
): string {
  if (!files.length) return message;
  const blocks = files.map(
    (file) =>
      `--- Attached file: ${file.name} ---\n${file.text}\n--- End of file: ${file.name} ---`
  );
  return `${message.trim()}\n\n${blocks.join("\n\n")}`;
}

export interface MessageAttachment {
  name: string;
  text: string;
}

export interface SplitMessage {
  input: string;
  attachments: MessageAttachment[];
}

const ATTACHMENT_BLOCK_RE =
  /--- Attached file: (.*?) ---\r?\n([\s\S]*?)\r?\n--- End of file: \1 ---/g;

// Inverso de buildMessageWithAttachments: separa el input del usuario
// de los bloques adjuntos para mostrarlos como chips colapsados en el
// transcript en lugar del contenido completo.
export function splitMessageAttachments(text: string): SplitMessage {
  const attachments: MessageAttachment[] = [];
  const input = (text || "")
    .replace(ATTACHMENT_BLOCK_RE, (_match, name, content) => {
      attachments.push({ name, text: content });
      return "";
    })
    .trim();
  return { input, attachments };
}
